import { ringPivot, transformAbout, type Ring } from './outlineGeometry';
import type { SiteBoundary } from './locate';

const MAX_SIDE_PX = 2600;
const MAX_SCALE = 3;

/**
 * Where the plan's four corners sit on the map for the outline as it is now. The corners were recorded for the
 * outline as first read, so they get the same resize and turn (about the outline's original centre) and the same
 * move. Null when there is no plan, or the outline was edited without its original centre being recorded.
 */
export function overlayCorners(b: Pick<SiteBoundary, 'ring' | 'plan' | 'adjust' | 'userEdited' | 'origin'>): Ring | null {
  if (!b.plan) return null;
  if (!b.userEdited && !b.adjust) return b.plan.corners;
  if (!b.origin) return null;
  const from = { lon: b.origin[0], lat: b.origin[1] };
  const adj = b.adjust ?? { scale: 1, rotationDeg: 0 };
  const turned = transformAbout(b.plan.corners, from, adj.scale, adj.rotationDeg);
  const now = ringPivot(b.ring);
  const dLon = now.lon - from.lon;
  const dLat = now.lat - from.lat;
  return turned.map(([lon, lat]) => [lon + dLon, lat + dLat]);
}

const rendered = new Map<string, Promise<string>>();

/** The plan's map frame drawn to a PNG, as an object URL the map can use as an image source. Cached per document, page and frame. */
export function renderPlanImage(documentId: string, page: number, frame: readonly number[]): Promise<string> {
  const key = `${documentId}|${page}|${frame.join(',')}`;
  let p = rendered.get(key);
  if (!p) {
    p = draw(documentId, page, frame);
    rendered.set(key, p);
    p.catch(() => rendered.delete(key));
  }
  return p;
}

async function draw(documentId: string, pageNo: number, frame: readonly number[]): Promise<string> {
  const [{ getPlanningDocumentDownloadUrl }, { openPdf }] = await Promise.all([
    import('./planningDocuments'),
    import('./pdfjs'),
  ]);
  const res = await fetch(await getPlanningDocumentDownloadUrl(documentId));
  if (!res.ok) throw new Error(`The location plan could not be downloaded (${res.status}).`);
  const pdf = await openPdf(new Uint8Array(await res.arrayBuffer()));
  try {
    const doc = await pdf.task.promise;
    const page = await doc.getPage(pageNo);
    const [x0, y0, x1, y1] = frame;
    const scale = Math.min(MAX_SCALE, MAX_SIDE_PX / Math.max(x1 - x0, y1 - y0));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round((x1 - x0) * scale));
    canvas.height = Math.max(1, Math.round((y1 - y0) * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('The location plan could not be drawn.');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, viewport, transform: [1, 0, 0, 1, -x0 * scale, -y0 * scale] }).promise;
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('The location plan could not be drawn.');
    return URL.createObjectURL(blob);
  } finally {
    void pdf.close();
  }
}
